import React, { useEffect, useRef, useState } from "react";
import {
    autocompleteCurrencyList,
    CurrencyCode,
    currencyCodeAsSymbol,
    type Price,
} from "../../common/currency";
import { isValidPrice } from "../../common/types/Booking";
import { Flex } from "./Flex";
import Icon from "./Icon";
import Input, { type InputActions } from "./Input";
import "./PriceInput.css";
import { isTypeOf } from "../../common/utils/zod.util";
import { useDebounce } from "../../utils/customHook.util";
import { takeIfAnyPredicateMatches } from "../../utils/filter.util";
import HandleClicksOutside from "../HandleClicksOutside";
import Divider from "./Divider";
import Label from "./Label";
import Paragraph from "./Paragraph";

type PriceProperties = {
    defaultCurrency?: CurrencyCode | null;
    price?: Price | null;
    paymentsPreferredCurrency?: string | null;
    onChange: (price: Price | null) => void;
    onChangeCurrency?: (currency: CurrencyCode) => void;
};

const currencies = autocompleteCurrencyList;
function PriceInput(props: PriceProperties) {
    const {
        defaultCurrency,
        price,
        paymentsPreferredCurrency,
        onChange,
        onChangeCurrency,
    } = props;
    const [currency, setCurrency] = useState(price?.currency || defaultCurrency);
    const [isOpen, setIsOpen] = useState(false);
    const [currencyWasChanged, setCurrencyWasChanged] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [amount, setAmount] = useState(price?.amount);
    const [enteredAmount, setEnteredAmount] = useState(price?.amount?.toString() || "");

    const debouncedSearchTerm = useDebounce<string | undefined>(searchTerm, 250);

    const searchInputRef = useRef<InputActions>(null);
    const priceInputRef = useRef<InputActions>(null);

    const [visibleItems, setVisibleItems] = useState(currencies);
    useEffect(() => {
        if (debouncedSearchTerm && debouncedSearchTerm.length >= 1) {
            setVisibleItems(
                takeIfAnyPredicateMatches(currencies, debouncedSearchTerm, [
                    (item) => item.text,
                ]),
            );
        } else {
            setVisibleItems(currencies);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setCurrency(price?.currency || defaultCurrency);
    }, [price, defaultCurrency]);

    const openDropdown = () => {
        setIsOpen(true);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    const closeDropdown = () => {
        setIsOpen(false);
        setSearchTerm("");
        if (currency && currencyWasChanged && !price) {
            setTimeout(() => {
                priceInputRef.current?.focus();
            }, 250);
        }
    };

    const triggerOnChange = (amount: number, currency: CurrencyCode) => {
        if (!currency) return;
        if (!amount) return;
        onChange({ currency, amount });
    };

    const triggerOnChangeCurrency = (currency: CurrencyCode) => {
        if (amount !== undefined) return;
        if (onChangeCurrency && isTypeOf(currency, CurrencyCode)) {
            onChangeCurrency(currency);
        }
    };

    return (
        <div className="price-input">
            <Flex.Row gap="large">
                <Flex.Row
                    onClick={() => {
                        openDropdown();
                    }}
                    className="price-input__price-selection"
                >
                    {isOpen ? (
                        <Input.PersistentTextField
                            ref={searchInputRef}
                            label="Currency"
                            value={searchTerm}
                            onChange={setSearchTerm}
                        />
                    ) : (
                        <Input.TextField
                            label="Currency"
                            placeholder="Select currency"
                            value={
                                currencies.filter((c) => c.id === currency)[0]?.text ??
                                ""
                            }
                            readOnly
                        />
                    )}
                    <Icon.ExpandMore.Small />
                </Flex.Row>
                <Divider axis="vertical" variant="solid" />
                <Flex.Row>
                    <Input.PersistentNumber
                        key={currency}
                        label="Total price"
                        value={enteredAmount}
                        placeholder="Enter price"
                        ref={priceInputRef}
                        prefix={currency ? currencyCodeAsSymbol(currency) : undefined}
                        onFocus={() => {
                            if (!currency) {
                                openDropdown();
                            }
                        }}
                        onBlur={(newValue) => {
                            setEnteredAmount(newValue);
                            if (
                                newValue !== "" &&
                                newValue !== "0" &&
                                isValidPrice(newValue)
                            ) {
                                setAmount(Number(newValue));
                                if (currency) {
                                    triggerOnChange(Number(newValue), currency);
                                }
                            } else {
                                setAmount(undefined);
                                onChange(null);
                            }
                        }}
                    />
                </Flex.Row>
            </Flex.Row>
            {isOpen && (
                <HandleClicksOutside
                    onClickOutside={() => {
                        closeDropdown();
                    }}
                    disabled={!isOpen}
                    className="price-input-dropdown"
                >
                    {!searchTerm && paymentsPreferredCurrency && (
                        <Flex.Column className="price-input-dropdown__member-preferred-currency">
                            <Label.Small.SemiBold.N50>
                                Member preferred currency:
                            </Label.Small.SemiBold.N50>
                            <Paragraph.Small>
                                {paymentsPreferredCurrency}
                            </Paragraph.Small>
                        </Flex.Column>
                    )}

                    <div className="price-input-dropdown__options">
                        {visibleItems.map((item) => (
                            <div key={item.id} className="price-input-dropdown__option">
                                <Input.Checkbox
                                    label={item.text}
                                    checked={currency ? currency === item.id : false}
                                    onChange={(_) => {
                                        if (isTypeOf(item.id, CurrencyCode)) {
                                            setCurrencyWasChanged(currency !== item.id);
                                            setCurrency(item.id);
                                            closeDropdown();
                                            if (amount) {
                                                triggerOnChange(amount, item.id);
                                            } else {
                                                triggerOnChangeCurrency(item.id);
                                            }
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </HandleClicksOutside>
            )}
        </div>
    );
}

export default PriceInput;
